import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../api/client';
import {
  Loader,
  AlertCircle,
  MapPin,
  Camera,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  User,
} from 'lucide-react';
import { useGlobalSearch } from '../context/SearchContext';
import { matchesGlobalSearch } from '../utils/globalSearch';
import { formatIstDateTime } from '../utils/formatIst';
import { resolveVisitImageSrc, visitHasPhoto } from '../utils/visitImage';

function formatDuration(checkIn, checkOut) {
  if (!checkIn || !checkOut) return '—';
  const start = new Date(String(checkIn).replace(' ', 'T'));
  const end = new Date(String(checkOut).replace(' ', 'T'));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '—';
  const mins = Math.max(0, Math.round((end - start) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatGps(lat, lng) {
  if (lat == null || lng == null) return '—';
  return `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
}

const VisitLogsManagement = () => {
  const [reps, setReps] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRepId, setSelectedRepId] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { query: globalSearch } = useGlobalSearch();

  const fetchList = async () => {
    try {
      setLoading(true);
      setError(null);
      const [repsRes, customersRes, visitsRes] = await Promise.all([
        adminAPI.getAllReps(),
        adminAPI.getAllCustomers(),
        adminAPI.getVisitLogs({
          rep_id: selectedRepId || undefined,
          customer_id: selectedCustomerId || undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
        }),
      ]);
      setReps(repsRes.data?.data || []);
      setCustomers(customersRes.data?.data || []);
      setVisits(visitsRes.data?.data || []);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to load visit history';
      setError(msg);
      setVisits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [selectedRepId, selectedCustomerId, fromDate, toDate]);

  const filteredVisits = useMemo(() => {
    return visits.filter((v) =>
      matchesGlobalSearch(globalSearch, [
        v.rep_name,
        v.rep_email,
        v.customer_name,
        v.notes,
        formatGps(v.lat_at_submission, v.lng_at_submission),
        v.check_in_time,
      ])
    );
  }, [visits, globalSearch]);

  const loadDetail = async (visitId) => {
    if (expandedId === visitId) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(visitId);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await adminAPI.getVisitLog(visitId);
      setDetail(res.data?.data || null);
    } catch (err) {
      setDetail(null);
      setError(err.response?.data?.error || err.message || 'Failed to load visit photos');
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading && visits.length === 0 && !error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-600" />
          <p className="text-gray-600">Loading visit history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Visit History</h2>
          <p className="text-gray-600">
            Site visits from the mobile app — customer, GPS, notes, and photos.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchList}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
        <p className="text-sm text-gray-500">Total visits</p>
        <p className="text-2xl font-bold text-gray-900">{filteredVisits.length}</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Representative</label>
            <select
              value={selectedRepId}
              onChange={(e) => setSelectedRepId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All reps</option>
              {reps.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Customer</label>
            <select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">From date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">To date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 admin-table-shell">
        <div className="admin-table-scroll">
          <table className="admin-data-table-xl">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 w-10" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rep</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Check-in</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Check-out</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">GPS</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Photos</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredVisits.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-600">
                    {visits.length === 0
                      ? 'No visits recorded yet.'
                      : 'No visits match your filters or search.'}
                  </td>
                </tr>
              ) : (
                filteredVisits.map((visit) => {
                  const isOpen = expandedId === visit.id;
                  return (
                    <React.Fragment key={visit.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => loadDetail(visit.id)}
                            className="text-gray-600 hover:text-gray-900 p-1"
                            title="View photos and details"
                          >
                            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{visit.rep_name || '—'}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{visit.customer_name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {formatIstDateTime(visit.check_in_time)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {formatIstDateTime(visit.checkout_time)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {formatDuration(visit.check_in_time, visit.checkout_time)}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-600">
                          {formatGps(visit.lat_at_submission, visit.lng_at_submission)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex gap-2">
                            {visit.has_visit_photo && (
                              <span className="text-blue-600" title="Site photo">
                                <Camera className="w-4 h-4 inline" /> Site
                              </span>
                            )}
                            {visit.has_selfie && (
                              <span className="text-purple-600" title="Selfie">
                                <User className="w-4 h-4 inline" /> Selfie
                              </span>
                            )}
                            {!visit.has_visit_photo && !visit.has_selfie && (
                              <span className="text-gray-400">—</span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                          {visit.notes || '—'}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-gray-50">
                          <td colSpan="9" className="px-6 py-4">
                            {detailLoading ? (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Loader className="w-5 h-5 animate-spin" />
                                Loading photos…
                              </div>
                            ) : detail && detail.id === visit.id ? (
                              <div className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                                  <p>
                                    <span className="font-semibold">Check-in:</span>{' '}
                                    {formatIstDateTime(detail.check_in_time || detail.visit_start_time)}
                                  </p>
                                  <p>
                                    <span className="font-semibold">Check-out:</span>{' '}
                                    {formatIstDateTime(detail.checkout_time || detail.visit_end_time)}
                                  </p>
                                </div>
                                {detail.notes && (
                                  <p className="text-sm text-gray-700">
                                    <span className="font-semibold">Notes:</span> {detail.notes}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  GPS at submission:{' '}
                                  {formatGps(
                                    detail.lat_at_submission ?? detail.location_lat,
                                    detail.lng_at_submission ?? detail.location_lon
                                  )}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {visitHasPhoto(detail, 'site') ? (
                                    <div>
                                      <p className="text-sm font-semibold text-gray-800 mb-2">Site photo</p>
                                      <img
                                        src={resolveVisitImageSrc(detail, 'site')}
                                        alt="Visit site"
                                        className="w-full max-h-80 object-contain rounded-lg border border-gray-200 bg-white"
                                      />
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-500">No site photo stored.</p>
                                  )}
                                  {visitHasPhoto(detail, 'selfie') ? (
                                    <div>
                                      <p className="text-sm font-semibold text-gray-800 mb-2">Selfie</p>
                                      <img
                                        src={resolveVisitImageSrc(detail, 'selfie')}
                                        alt="Visit selfie"
                                        className="w-full max-h-80 object-contain rounded-lg border border-gray-200 bg-white"
                                      />
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-500">No selfie stored.</p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">Could not load visit details.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VisitLogsManagement;
